import { Injectable, inject } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { RouteMessage } from "@app/models/dto/route-message";
import { LocationModel } from "@app/models/rest/location";
import { PostCodeMessage } from "@app/models/dto/postcode-message";
import { PostCodeSearchPage } from "@app/models/dto/postcode-search-page";
import { ZoneLookupMessage } from "@app/models/dto/zone-lookup-message";
import { ClientConfigService } from "@app/core/config/client-config.service";
import { firstValueFrom } from "rxjs";
import { ProjectionRelType } from "@lagoshny/ngx-hateoas-client";

@Injectable({
  providedIn: "root"
})
export class RouteService {
  private clientConfigService = inject(ClientConfigService);
  private http = inject(HttpClient);

  public async checkPostcode(postcode: string): Promise<PostCodeMessage> {
    const params = new HttpParams().set("query", postcode);
    const config = await this.clientConfigService.config;
    const result = await firstValueFrom(
      this.http.get(config.serviceUrl + "/api/v1/postcode", {
        withCredentials: true,
        params
      })
    );
    return result as unknown as PostCodeMessage;
  }

  public async searchPostcodes(
    query: string,
    page: number = 0,
    pageSize: number = 10
  ): Promise<PostCodeSearchPage> {
    const params = new HttpParams()
      .set("query", query)
      .set("page", page.toString())
      .set("pageSize", pageSize.toString());

    const config = await this.clientConfigService.config;
    const result = await firstValueFrom(
      this.http.get(config.serviceUrl + "/api/v1/postcode/search", {
        withCredentials: true,
        params
      })
    );
    return result as unknown as PostCodeSearchPage;
  }

  public async lookupZonesByPostcode(
    postcode: string
  ): Promise<ZoneLookupMessage> {
    const params = new HttpParams().set("query", postcode);
    const config = await this.clientConfigService.config;
    const result = await firstValueFrom(
      this.http.get(config.serviceUrl + "/api/v1/postcode/zones", {
        withCredentials: true,
        params
      })
    );
    return result as unknown as ZoneLookupMessage;
  }

  public async getRoute(
    locationA: LocationModel | ProjectionRelType<LocationModel>,
    locationB: LocationModel | ProjectionRelType<LocationModel>
  ): Promise<RouteMessage> {
    const params = new HttpParams()
      .set("x1", locationA.longitude.toString(10))
      .set("y1", locationA.latitude.toString(10))
      .set("x2", locationB.longitude.toString(10))
      .set("y2", locationB.latitude.toString(10));

    const config = await this.clientConfigService.config;
    const result = await firstValueFrom(
      this.http.get(config.serviceUrl + "/api/v1/route", {
        withCredentials: true,
        params
      })
    );
    return result as unknown as RouteMessage;
  }

  public async getNextTicketRoute(
    ticketId: number,
    resourceAvailabilityId: number
  ): Promise<RouteMessage> {
    const params = new HttpParams()
      .set("ticketId", ticketId ? String(ticketId) : "")
      .set(
        "resourceAvailabilityId",
        resourceAvailabilityId ? String(resourceAvailabilityId) : ""
      );
    const config = await this.clientConfigService.config;
    const result = await firstValueFrom(
      this.http.get(config.serviceUrl + "/api/v1/routeNextTicket", {
        withCredentials: true,
        params
      })
    );
    return result as unknown as RouteMessage;
  }
}
